import React, { useState } from "react";
import {
  Table,
  Badge,
  ScrollArea,
  createStyles,
  Avatar,
  Text,
  Skeleton,
  Center,
  Stack,
} from "@mantine/core";
import { useGetDirectorTeamAttendance } from "../../../api/attendance/getDirectorTeamAttendance";

const useStyles = createStyles((theme) => ({
  header: {
    position: "sticky",
    top: 0,
    backgroundColor:
      theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
    transition: "box-shadow 150ms ease",
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      borderBottom: `1px solid ${
        theme.colorScheme === "dark"
          ? theme.colors.dark[3]
          : theme.colors.gray[2]
      }`,
    },
  },
  scrolled: {
    boxShadow: theme.shadows.sm,
  },
}));

// Helper function to get badge color based on status
const getBadgeColor = (status: string): string => {
  switch (status) {
    case "Present":
      return "teal";
    case "Late":
      return "yellow";
    case "Leave":
      return "blue";
    case "Absent":
      return "red";
    default:
      return "gray";
  }
};

interface DirectorAttendanceTableProps {
  filterType: string;
  dateRange: [Date | null, Date | null];
}

export function DirectorAttendanceTable({
  filterType,
  dateRange,
}: DirectorAttendanceTableProps) {
  const { classes } = useStyles();
  const [page] = useState(1);
  const [limit] = useState(10);

  // Convert date range to string format for API
  const startDate =
    dateRange[0] && filterType === "Custom Range"
      ? dateRange[0].toISOString().split("T")[0]
      : null;
  const endDate =
    dateRange[1] && filterType === "Custom Range"
      ? dateRange[1].toISOString().split("T")[0]
      : null;

  const { data: attendanceResponse, isLoading } = useGetDirectorTeamAttendance(
    page,
    limit,
    filterType,
    startDate,
    endDate
  );

  if (isLoading) {
    return (
      <ScrollArea>
        <Stack spacing="md">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={60} radius="md" />
          ))}
        </Stack>
      </ScrollArea>
    );
  }

  const attendanceData = attendanceResponse?.data?.records || [];

  const rows = attendanceData.map((row) => (
    <tr key={row.id}>
      <td>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar src={row.avatar} radius="xl" size="sm" />
          <div>
            <Text size="sm" fw={500}>
              {row.name}
            </Text>
            <Text size="xs" c="dimmed">
              {row.role}
            </Text>
          </div>
        </div>
      </td>
      <td>
        <Badge color={getBadgeColor(row.status)}>{row.status}</Badge>
      </td>
      <td>{row.department}</td>
      <td>{row.punchInTime || "-"}</td>
      <td>{row.punchOutTime || "-"}</td>
      <td>{row.totalWorkingHours}</td>
    </tr>
  ));

  if (attendanceData.length === 0) {
    return (
      <Center py="xl">
        <Text c="dimmed">No attendance records found</Text>
      </Center>
    );
  }

  return (
    <ScrollArea>
      <Table style={{ minWidth: 800 }} verticalSpacing="sm">
        <thead className={classes.header}>
          <tr>
            <th>Employee</th>
            <th>Status</th>
            <th>Department</th>
            <th>Check In</th>
            <th>Check Out</th>
            <th>Production Hours</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </Table>
    </ScrollArea>
  );
}
